import { useEffect } from 'preact/hooks';
import type { Signal as SignalType } from '@preact/signals';
import * as CartService from '@/widgets/services/CartService.ts';
import type { Subscription } from '@/domain/Subscription.ts';
import type { Product } from '@/domain/Product.ts';

type CartServiceResult = {
  cart: SignalType<Cart | undefined>;
  isLoading: SignalType<boolean>;
  initCart(napi: NAPI): () => void;
  addSubscription(productId: string, subscriptionId: string): Promise<boolean>;
  removeSubscription(): Promise<boolean>;
  updateItem(productId: string, amount: number): Promise<boolean>;
  isProductAvailable(productId: string): Promise<boolean>;
  getSubscription(
    subscriptionsByDuration: {
      [duration: string]: Subscription[];
    },
    subscriptionId?: string
  ): Subscription | false;
  isReady: SignalType<boolean>;
};

export function useCart(): CartServiceResult {
  useEffect(() => {
    const emptyCleanup = () => {};
    const cleanup = CartService.cart.peek()
      ? emptyCleanup
      : CartService.initCart();

    return cleanup;
  }, []);

  return {
    ...CartService
  };
}

export function useCartInProduct(
  product: Product,
  onInitQuantity: (quantity: number) => void
) {
  useEffect(() => {
    let timeout: ReturnType<typeof setInterval>;

    const saveCurrentQuantity = async () => {
      const cart = await window.napi.cart().getCartFromStorage();
      const details = cart.cartLines.find(
        ({ productId }) => productId === product.id
      );
      const quantity = details?.quantity;

      if (typeof quantity !== undefined) {
        onInitQuantity(quantity as number);
      }
    };

    const initialize = () => {
      if (window.CartManager && window.napi) {
        saveCurrentQuantity();
        window.napi.data().on('cart.update', saveCurrentQuantity);
        clearTimeout(timeout);
      } else {
        timeout = setTimeout(initialize, 0);
      }
    };

    timeout = setTimeout(initialize, 0);

    return () => {
      clearTimeout(timeout);
      window.napi?.data().off('cart.update', saveCurrentQuantity);
    };
  }, [product.id]);

  return product.inStock && product.isOrderable;
}
